import { BaseStreaming } from '../Streaming';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingService from '../../abstract/StreamingService';
import { AlertSubscribeRequest, getAlertUnsubscribeRequest } from './Messages';
import AlertSubscription, {
  AlertOnSubscribe,
  AlertOnUpdate,
} from './AlertSubscription';

export class AlertStreaming extends StreamingService<DoryServiceType.ALERT> {
  private available = true;

  public get isAvailable(): boolean {
    return this.available;
  }
  /**
   * @hidden
   */
  public set isAvailable(val: boolean) {
    if (val && !this.available) {
      this.available = val;
      this.resubscribe();
    } else {
      this.available = val;
    }
  }
  private subscriptions?: AlertSubscription = undefined;

  /**
   * @hidden
   */
  constructor(socket: BaseStreaming) {
    super(DoryServiceType.ALERT, 'alerts', socket);
  }

  /**
   * Subscribe to websocket updates.
   * There can only be one AlertStreaming subscription
   *
   * ```js example
   * dory.Streaming.connect()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function () {
   *     dory.Streaming.Alert.subscribe(
   *       // Subscribe Callback
   *       function (data, err) {
   *         if (err) {
   *           console.error(err);
   *         } else {
   *           console.log('subscribed', data);
   *         }
   *       },
   *       // Update Callback
   *       function (data) {
   *         console.log('update', data);
   *       }
   *     );
   *
   *     // Unsubscribe after 10 seconds
   *     window.setTimeout(function () {
   *       dory.Streaming.Alert.unsubscribe();
   *       dory.Streaming.disconnect();
   *     }, 10000);
   *   });
   * ```
   *
   * @param subscribe  Executed when subscribe response is received
   * @param update  Executed when update is received
   */
  public subscribe(subscribe: AlertOnSubscribe, update: AlertOnUpdate): void {
    const source = 'Streaming.Alert.subscribe';
    const missingParams =
      subscribe === undefined || update === undefined
        ? 'Missing required callback, "subscribe" requires callbacks for: \n- subscribe(data, error)\n- update(data, error)'
        : '';

    const errorCallback = this.getSubscribeErrorHandler(
      source,
      subscribe,
      undefined, // id
      this.subscriptions !== undefined,
      missingParams
    );
    if (errorCallback) {
      // do nothing.
    } else {
      // Create subscription object
      const subscription = new AlertSubscription(
        '',
        this.SOCKET.EventBus,
        subscribe,
        update
      );
      // Store subscription
      this.subscriptions = subscription;
      // Setup subscription
      subscription.start(this.SOCKET.Timeout);
      // Start subscription
      this.SOCKET.send<AlertSubscribeRequest>(subscription.SubscribeRequest);
    }
  }

  /**
   * Unsubscribe from websocket updates
   * @param callback  Executed when unsubscribe response is received
   */
  public unsubscribe(
    callback?: (e: unknown, err?: StreamingError) => void
  ): void {
    const source = 'Streaming.Alert.unsubscribe';

    if (!this.SOCKET.isConnected) {
      this.RaiseNotConnectedError(source, callback);
    } else if (!this.subscriptions) {
      if (callback) {
        callback(
          undefined,
          new StreamingError(
            source,
            StreamingErrorCodes.INVALID_REQUEST,
            `Not subscribed to Alert Streaming!`
          )
        );
      }
    } else {
      // Remove subscription from storage
      const message = getAlertUnsubscribeRequest();
      this.subscriptions.stop();
      this.subscriptions = undefined;

      if (callback) {
        this.SOCKET.EventBus.once(
          DoryServiceType.ALERT,
          DoryMessageType.UNSUBSCRIBE,
          this.setTimeout(source, this.SOCKET.Timeout, callback)
        );
      }

      this.SOCKET.send(message);
    }
  }

  /**
   * @hidden
   * Resubscribe to all previous subscriptions
   */
  public resubscribe(): void {
    if (this.subscriptions) {
      this.subscriptions.restart(this.SOCKET.Timeout);
      this.SOCKET.send<AlertSubscribeRequest>(
        this.subscriptions.SubscribeRequest
      );
    }
  }
  /**
   * @hidden
   * Clear all stored subscription ids
   */
  public clearSubscriptions(): void {
    this.subscriptions = undefined;
  }
}
